import React, { useEffect, useState } from 'react';
import { clientsApi, ClientCategory } from '../services/api';
import { Button } from '../ds/components/Button';
import { InputField } from '../ds/InputField';
import { ModalShell } from '../ds/ModalShell';

/**
 * 068 (raport uwag Bronka §C, C3): zarządzanie listą kategorii zarządców — dodawanie, zmiana nazwy,
 * wyłączanie/włączanie i kolejność. Kategorii się nie kasuje: wyłączona znika z list wyboru, a
 * zarządcy, którzy ją mają, zostają z nią (jak listy urządzeń w „Zaleceniach"). Zapis: admin/superadmin
 * (backend 401/403 dla innych — błąd pokazujemy w modalu).
 */
type Props = {
  open: boolean;
  categories: ClientCategory[];
  onClose: () => void;
  /** Po każdej udanej zmianie — rodzic przeładowuje kategorie i zarządców. */
  onChanged: () => Promise<void> | void;
};

const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-end', gap: 'var(--space-2)',
  padding: 'var(--space-2) 0', borderBottom: 'var(--border-width) solid var(--border)',
};
const smallBtn: React.CSSProperties = { minHeight: 'var(--touch-min)', padding: '0 var(--space-3)', flexShrink: 0 };

const ClientCategoriesModal: React.FC<Props> = ({ open, categories, onClose, onChanged }) => {
  const [names, setNames] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setNames(Object.fromEntries(categories.map((c) => [c.id, c.name])));
  }, [open, categories]);

  useEffect(() => { if (open) { setError(null); setNewName(''); } }, [open]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (e: any) {
      setError(e?.message || 'Nie udało się zapisać kategorii');
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    const name = newName.trim();
    if (!name) { setError('Wpisz nazwę kategorii'); return; }
    void run(async () => { await clientsApi.createCategory(name); setNewName(''); });
  };

  const rename = (c: ClientCategory) => {
    const name = (names[c.id] ?? '').trim();
    if (!name) { setError('Nazwa kategorii nie może być pusta'); return; }
    if (name === c.name) return;
    void run(() => clientsApi.updateCategory(c.id, { name }));
  };

  // Kolejność ustawia się w obrębie firmy kategorii (lista jest per firma).
  const move = (c: ClientCategory, delta: -1 | 1) => {
    const same = categories.filter((x) => x.companyId === c.companyId);
    const idx = same.findIndex((x) => x.id === c.id);
    const target = idx + delta;
    if (idx < 0 || target < 0 || target >= same.length) return;
    const order = same.map((x) => x.id);
    [order[idx], order[target]] = [order[target], order[idx]];
    void run(() => clientsApi.reorderCategories(order));
  };

  return (
    <ModalShell
      open={open}
      title="Kategorie zarządców"
      width={620}
      onClose={() => { if (!busy) onClose(); }}
      footer={<Button variant="secondary" onClick={onClose} disabled={busy}>Zamknij</Button>}
    >
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
          Kategorii się nie usuwa — wyłączona znika z listy wyboru, a zarządcy, którzy ją mają, zostają z nią.
        </p>
        {error && (
          <div
            role="alert"
            style={{
              padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', background: 'var(--status-deny-bg)',
              color: 'var(--status-deny)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-label)',
            }}
          >
            {error}
          </div>
        )}
        <div>
          {categories.length === 0 && (
            <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body)' }}>Brak kategorii.</div>
          )}
          {categories.map((c) => {
            const same = categories.filter((x) => x.companyId === c.companyId);
            const pos = same.findIndex((x) => x.id === c.id);
            return (
              <div key={c.id} style={rowStyle} data-testid={`client-category-${c.id}`}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <InputField
                    label={`Nazwa${c.active ? '' : ' (wyłączona)'}`}
                    value={names[c.id] ?? ''}
                    maxLength={60}
                    disabled={busy}
                    onChange={(e) => setNames({ ...names, [c.id]: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') rename(c); }}
                    hint={`Zarządców: ${c.clientsCount ?? 0}`}
                  />
                </div>
                <Button
                  variant="ghost"
                  icon="check"
                  disabled={busy || (names[c.id] ?? '').trim() === c.name}
                  onClick={() => rename(c)}
                  aria-label={`Zapisz nazwę kategorii ${c.name}`}
                  style={smallBtn}
                >
                  Zapisz
                </Button>
                <Button
                  variant="ghost"
                  icon="chevron-up"
                  disabled={busy || pos <= 0}
                  onClick={() => move(c, -1)}
                  aria-label={`Przesuń kategorię ${c.name} wyżej`}
                  style={smallBtn}
                >
                  {null}
                </Button>
                <Button
                  variant="ghost"
                  icon="chevron-down"
                  disabled={busy || pos >= same.length - 1}
                  onClick={() => move(c, 1)}
                  aria-label={`Przesuń kategorię ${c.name} niżej`}
                  style={smallBtn}
                >
                  {null}
                </Button>
                <Button
                  variant="ghost"
                  disabled={busy}
                  onClick={() => void run(() => clientsApi.updateCategory(c.id, { active: !c.active }))}
                  aria-label={`${c.active ? 'Wyłącz' : 'Włącz'} kategorię ${c.name}`}
                  style={smallBtn}
                >
                  {c.active ? 'Wyłącz' : 'Włącz'}
                </Button>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-2)' }}>
          <div style={{ flex: 1 }}>
            <InputField
              label="Nowa kategoria"
              placeholder="np. SPÓŁDZIELNIE"
              value={newName}
              maxLength={60}
              disabled={busy}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            />
          </div>
          <Button variant="primary" icon="plus" onClick={add} disabled={busy || !newName.trim()} style={smallBtn}>
            Dodaj
          </Button>
        </div>
      </div>
    </ModalShell>
  );
};

export default ClientCategoriesModal;
